import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  Section,
} from '@wodi/ui';
import { useState } from 'react';

import { TableScroll } from '../layout/screen.tsx';
import { can, sessionQuery } from '../api/session.ts';
import { setLevels, stockCardQuery, storeItemQuery } from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatDateOnly, formatIsoDate, formatTimeOnly } from '../patients/format.ts';
import { BackToStore, errorText, WithStore } from '../stock/common.tsx';
import {
  expiryWords,
  formatQuantity,
  MOVEMENT_LABELS,
  readQuantity,
  REORDER_LABELS,
} from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/stores/$storeId/items/$itemId');

/**
 * Stock card (plan §5.2): the batches this store holds in FEFO order, the reorder levels,
 * and every movement with the ledger's running balance, like a paper bin card.
 */
export function StockItemPage() {
  const { storeId, itemId } = route.useParams();
  return (
    <WithStore storeId={storeId}>
      {(store) => <StockItemBody storeId={store.id} storeName={store.name} itemId={itemId} />}
    </WithStore>
  );
}

function StockItemBody({
  storeId,
  storeName,
  itemId,
}: {
  storeId: string;
  storeName: string;
  itemId: string;
}) {
  const detail = useQuery(storeItemQuery(storeId, itemId));
  const card = useQuery(stockCardQuery(storeId, itemId));
  const { data: session } = useSuspenseQuery(sessionQuery);

  if (detail.isPending) return <LoadingBlock label="Loading item" rows={4} />;
  if (detail.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void detail.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(detail.error, "This item couldn't be loaded. Try again.")}
      </Notice>
    );
  const d = detail.data;
  const unit = d.item.stock?.baseUnit.display ?? '';
  return (
    <div className={styles.page}>
      <BackToStore storeId={storeId} name={storeName} />
      <PageHeader title={d.item.name} />
      <dl className={styles.facts}>
        <dt>Item code</dt>
        <dd className="wodi-id">{d.item.code}</dd>
        <dt>Usable in {storeName}</dt>
        <dd className={styles.numbers}>{formatQuantity(d.usable, unit)}</dd>
        <dt>Status</dt>
        <dd>
          <Tag
            state={d.reorderStatus === 'ok' || d.reorderStatus === 'not_set' ? 'done' : 'attention'}
          >
            {REORDER_LABELS[d.reorderStatus]}
          </Tag>
        </dd>
        {d.item.stock?.isControlled === true ? (
          <>
            <dt>Controlled drug</dt>
            <dd>Yes. The controlled-drug register comes in a later release.</dd>
          </>
        ) : null}
      </dl>

      <Section title="Batches">
        <TableScroll>
          <Table flat aria-label="Batches in this store" dense>
            <TableHeader>
              <Column isRowHeader>Batch</Column>
              <Column>Expiry</Column>
              <Column>When</Column>
              <Column numeric>Quantity</Column>
              <Column>Issue order</Column>
            </TableHeader>
            <TableBody
              items={d.batches.map((b) => ({ ...b, id: b.batchId }))}
              renderEmptyState={() => 'This store holds none of this item.'}
            >
              {(b) => (
                <Row id={b.batchId}>
                  <Cell>
                    <span className="wodi-id">{b.batchNumber}</span>
                  </Cell>
                  <Cell>{b.expiryDate === null ? 'No expiry' : formatIsoDate(b.expiryDate)}</Cell>
                  <Cell>{expiryWords(b.daysToExpiry)}</Cell>
                  <Cell numeric>{formatQuantity(b.quantity, unit)}</Cell>
                  <Cell>
                    {b.fefoRank === null
                      ? 'Expired: write off'
                      : b.fefoRank === 1
                        ? 'Next to issue'
                        : `${b.fefoRank}`}
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>

      {session !== null && can(session, 'stock.manage') ? (
        <Levels
          storeId={storeId}
          itemId={itemId}
          reorderLevel={d.reorderLevel}
          maxLevel={d.maxLevel}
        />
      ) : (
        <p>
          Reorder level:{' '}
          {d.reorderLevel === null ? 'not set' : formatQuantity(d.reorderLevel, unit)}
        </p>
      )}

      <Section title="Stock card">
        {card.isPending ? <LoadingBlock label="Loading stock card" rows={4} /> : null}
        {card.isError ? (
          <Notice tone="error">
            {errorText(card.error, "The stock card couldn't be loaded.")}
          </Notice>
        ) : null}
        {card.isSuccess ? (
          <TableScroll>
            <Table flat aria-label="Stock card, newest first" dense>
              <TableHeader>
                <Column isRowHeader>Date</Column>
                <Column>Time</Column>
                <Column>What happened</Column>
                <Column>Batch</Column>
                <Column numeric>In</Column>
                <Column numeric>Out</Column>
                <Column numeric>Balance</Column>
                <Column>By</Column>
              </TableHeader>
              <TableBody
                items={card.data.items.map((r) => ({ ...r, id: r.movementId }))}
                renderEmptyState={() => 'No movements yet.'}
              >
                {(r) => (
                  <Row id={r.movementId}>
                    <Cell>{formatDateOnly(r.occurredAt)}</Cell>
                    <Cell>{formatTimeOnly(r.occurredAt)}</Cell>
                    <Cell>
                      {MOVEMENT_LABELS[r.kind]}
                      {r.documentNumber === null ? null : (
                        <>
                          {', '}
                          <span className="wodi-id">{r.documentNumber}</span>
                        </>
                      )}
                    </Cell>
                    <Cell>
                      <span className="wodi-id">{r.batchNumber}</span>
                    </Cell>
                    <Cell numeric>{r.quantity > 0 ? String(r.quantity) : ''}</Cell>
                    <Cell numeric>{r.quantity < 0 ? String(-r.quantity) : ''}</Cell>
                    <Cell numeric>{String(r.balanceAfter)}</Cell>
                    <Cell>{r.recordedBy}</Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
    </div>
  );
}

function Levels({
  storeId,
  itemId,
  reorderLevel,
  maxLevel,
}: {
  storeId: string;
  itemId: string;
  reorderLevel: number | null;
  maxLevel: number | null;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [reorder, setReorder] = useState(reorderLevel === null ? '' : String(reorderLevel));
  const [max, setMax] = useState(maxLevel === null ? '' : String(maxLevel));
  const reorderValue = readQuantity(reorder);
  const maxValue = max.trim() === '' ? null : readQuantity(max);
  const valid =
    reorderValue !== null && (max.trim() === '' || (maxValue !== null && maxValue >= reorderValue));
  const save = useMutation({
    mutationFn: () =>
      setLevels(storeId, itemId, { reorderLevel: reorderValue ?? 0, maxLevel: maxValue }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stock', storeId] });
      toasts.show({ title: 'Levels saved' });
    },
  });
  return (
    <form
      className={styles.form}
      aria-labelledby="levels"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <h2 id="levels" className={styles.sectionHeading}>
        Reorder levels
      </h2>
      <div className={styles.row}>
        <TextField
          label="Reorder level"
          description="List the item as low when usable stock is at or below this."
          inputMode="numeric"
          value={reorder}
          onChange={setReorder}
          {...(reorder !== '' && reorderValue === null
            ? { errorMessage: 'Enter a whole number.' }
            : {})}
        />
        <TextField
          label="Maximum (optional)"
          inputMode="numeric"
          value={max}
          onChange={setMax}
          {...(max !== '' && !valid ? { errorMessage: 'At least the reorder level.' } : {})}
        />
      </div>
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, "The levels weren't saved. Try again.")}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!valid || !online}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save levels
        </Button>
      </div>
    </form>
  );
}
